import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { toast } from 'sonner';

interface NotificationPreference {
  id: string;
  userId: string;
  emailEnabled: boolean;
  slackEnabled: boolean;
  pushEnabled: boolean;
  emailProjectAssignment: boolean;
  emailTaskAssignment: boolean;
  emailComments: boolean;
  emailDeadlines: boolean;
  emailMentions: boolean;
  slackWebhookUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

interface NotificationContextType {
  preferences: NotificationPreference | null;
  isLoading: boolean;
  updatePreferences: (updates: Partial<NotificationPreference>) => Promise<void>;
  sendTestNotification: (type: 'project_assignment' | 'task_assignment' | 'comment' | 'mention' | 'deadline_reminder') => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [preferences, setPreferences] = useState<NotificationPreference | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: async () => {
      return api.get<NotificationPreference>('/notifications/preferences');
    },
  });

  useEffect(() => {
    if (data) {
      setPreferences(data);
    }
  }, [data]);

  const updatePreferencesMutation = useMutation({
    mutationFn: async (updates: Partial<NotificationPreference>) => {
      return api.patch<NotificationPreference>('/notifications/preferences', updates);
    },
    onSuccess: (data) => {
      setPreferences(data);
      queryClient.setQueryData(['notificationPreferences'], data);
      toast.success('Notification preferences updated');
    },
    onError: () => {
      toast.error('Failed to update notification preferences');
    },
  });

  const testNotificationMutation = useMutation({
    mutationFn: async (type: 'project_assignment' | 'task_assignment' | 'comment' | 'mention' | 'deadline_reminder') => {
      return api.post('/notifications/test', { type });
    },
    onSuccess: () => {
      toast.success('Test notification sent!');
    },
    onError: () => {
      toast.error('Failed to send test notification');
    },
  });

  const updatePreferences = async (updates: Partial<NotificationPreference>) => {
    await updatePreferencesMutation.mutateAsync(updates);
  };

  const sendTestNotification = async (type: 'project_assignment' | 'task_assignment' | 'comment' | 'mention' | 'deadline_reminder') => {
    await testNotificationMutation.mutateAsync(type);
  };

  return (
    <NotificationContext.Provider
      value={{
        preferences,
        isLoading,
        updatePreferences,
        sendTestNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

